import type { RunEtaPayload } from "@the-drop/types";
import { useEffect, useState } from "react";
import { useDispensarySocketInstance } from "../workspace/dispensary-socket-context";

/**
 * Listens for **`run.eta`** on the shared dispensary socket and accumulates the latest payload per
 * `runId`. Returns a snapshot map the active-runs UI can index by `run.runId` to surface
 * stops-ahead + per-order ETA windows inline.
 *
 * **Audience scoping reminder:** `run.eta` is in `CUSTOMER_NAMESPACE_DENYLIST` (PR #374) — it
 * never reaches customer namespaces, so consuming it here on the dispensary socket is safe.
 *
 * Single-responsibility hook (separate from `useDispensaryDashboardLive` which mutates React
 * Query caches). Mirrors the transporter side's `useTransporterRunEta`.
 */
export function useDispensaryRunEta(dispensaryId: string | null): Record<string, RunEtaPayload> {
  const socket = useDispensarySocketInstance();
  const [runEtaByRunId, setRunEtaByRunId] = useState<Record<string, RunEtaPayload>>({});

  useEffect(() => {
    if (!socket || !dispensaryId) return;

    const onRunEta = (raw: unknown) => {
      const p = raw as Partial<RunEtaPayload> | null;
      if (
        p == null ||
        typeof p.runId !== "string" ||
        !Array.isArray(p.stops) ||
        typeof p.computedAt !== "string"
      ) {
        // Malformed payload — skip rather than corrupt state.
        return;
      }
      const runId = p.runId;
      const accepted: RunEtaPayload = {
        runId,
        dispensaryId: typeof p.dispensaryId === "string" ? p.dispensaryId : undefined,
        transporterId: typeof p.transporterId === "string" ? p.transporterId : undefined,
        stops: p.stops.map((s) => ({
          orderId: String(s.orderId ?? ""),
          stopsAhead: Number(s.stopsAhead ?? 0),
          etaStart: String(s.etaStart ?? ""),
          etaEnd: String(s.etaEnd ?? ""),
        })),
        computedAt: p.computedAt,
      };
      setRunEtaByRunId((prev) => ({ ...prev, [runId]: accepted }));
    };

    socket.on("run.eta", onRunEta);
    return () => {
      socket.off("run.eta", onRunEta);
    };
  }, [socket, dispensaryId]);

  return runEtaByRunId;
}
